import { useEffect, useState } from 'react';
import cls from 'classnames';
import { Image, Button } from 'react-bootstrap';
import { useModalDialog, ConfirmModalDialog } from 'src/components/base/ModalDialog';
import { ImageLightbox } from 'src/components/base/ImageLightbox';
import { downloadImageFile } from 'src/utils/download';
import classes from './ImageGalleryPanel.module.scss';

const DEFAULT_MAX_IMAGES = 3;

export const ImageGalleryPanel = ({
  images = [],
  maxImages = DEFAULT_MAX_IMAGES,
  allowUpload = true,
  allowDeletePhoto = true,
  allowDownload = true,
  showDownloadAll = false,
  onAddImage,
  onDeleteImage,
  onDownloadImage,
  onDownloadAll,
  isLoading = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const lightboxModal = useModalDialog();
  const deleteModal = useModalDialog();

  const count = images.length;
  const selectedIndex = count ? Math.min(currentIndex, count - 1) : 0;
  const selectedImage = images[selectedIndex] ?? {};
  const hasPrevious = selectedIndex > 0;
  const hasNext = selectedIndex < count - 1;
  const atLimit = count >= maxImages;

  useEffect(() => {
    if (currentIndex >= count && count > 0) {
      setCurrentIndex(count - 1);
    }
  }, [count, currentIndex]);

  function downloadImage(image, index) {
    if (typeof onDownloadImage === 'function') {
      return onDownloadImage(image, index);
    }
    return downloadImageFile(image, index);
  }

  function handleOpenLightbox() {
    if (!count) { return; }
    lightboxModal.show({
      index: selectedIndex,
    });
  }

  async function handleDeleteClick() {
    if (!onDeleteImage) { return; }
    if (await deleteModal.show({ text: __('Do you want to delete this image?') })) {
      await onDeleteImage(selectedImage, selectedIndex);
    }
  }

  async function handleDownloadAll() {
    if (typeof onDownloadAll === 'function') {
      await onDownloadAll(images);
    }
  }

  if (!count) {
    return (
      <div className={classes.gallery}>
        <div className={classes.empty}>
          <div className={classes.emptyIcon}><i className="bi bi-image" /></div>
          <p>{allowUpload ? __('No photos yet. Add one to document this catch.') : __('No photos for this catch.')}</p>
          {allowUpload && !isLoading && (
            <Button variant="outline-primary" onClick={onAddImage}>{__('Add photo')}</Button>
          )}
        </div>
        <ImageLightbox
          {...lightboxModal.register()}
          images={images}
          onDelete={allowDeletePhoto ? onDeleteImage : undefined}
          onDownload={allowDownload ? downloadImage : undefined}
        />
        {allowDeletePhoto && <ConfirmModalDialog {...deleteModal.register()} />}
      </div>
    );
  }

  const slotsLeft = maxImages - count;

  return (
    <div className={classes.gallery}>
      <div className={classes.stage}>
        {allowDeletePhoto && (
          <button type="button" className={classes.deleteButton} onClick={handleDeleteClick} aria-label={__('Delete this photo')}>
            <i className="bi bi-trash" />
          </button>
        )}
        <span className={classes.counter}>{selectedIndex + 1} / {count}</span>
        <Image
          src={selectedImage.file_url ?? selectedImage.thumb_url}
          alt={selectedImage.name}
          className={classes.stageImage}
          onClick={handleOpenLightbox}
        />
        {hasPrevious && (
          <button type="button" className={cls(classes.arrow, classes.prev)} onClick={() => setCurrentIndex(selectedIndex - 1)} aria-label={__('Previous')}>‹</button>
        )}
        {hasNext && (
          <button type="button" className={cls(classes.arrow, classes.next)} onClick={() => setCurrentIndex(selectedIndex + 1)} aria-label={__('Next')}>›</button>
        )}
      </div>

      <div className={classes.thumbs}>
        {images.map((image, index) => (
          <button
            key={image.id ?? index}
            type="button"
            className={cls(classes.thumb, { [classes.thumbActive]: index === selectedIndex })}
            onClick={() => setCurrentIndex(index)}
          >
            <Image src={image.thumb_url ?? image.file_url} alt={image.name} />
          </button>
        ))}
        {allowUpload && !atLimit && !isLoading && (
          <button type="button" className={classes.addThumb} onClick={onAddImage} aria-label={__('Add photo')}>
            <i className="bi bi-plus-lg" />
          </button>
        )}
      </div>

      <div className={cls(classes.limit, { [classes.limitFull]: atLimit })}>
        {allowUpload
          ? (atLimit
            ? __('Maximum of {count} photos reached').replace('{count}', maxImages)
            : __('{count} of {max} photos · {left} slot(s) left')
              .replace('{count}', count)
              .replace('{max}', maxImages)
              .replace('{left}', slotsLeft))
          : __('{count} of {max} photos').replace('{count}', count).replace('{max}', maxImages)}
      </div>

      {allowDownload && (
        <div className={classes.actions}>
          <Button variant="outline-primary" onClick={() => downloadImage(selectedImage, selectedIndex)}>
            {__('Download this photo')}
          </Button>
          {showDownloadAll && count > 1 && (
            <Button variant="primary" onClick={handleDownloadAll}>
              {__('Download all ({count})').replace('{count}', count)}
            </Button>
          )}
        </div>
      )}

      <ImageLightbox
        {...lightboxModal.register()}
        images={images}
        onDelete={allowDeletePhoto ? onDeleteImage : undefined}
        onDownload={allowDownload ? downloadImage : undefined}
      />
      {allowDeletePhoto && <ConfirmModalDialog {...deleteModal.register()} />}
    </div>
  );
};
